import { StudentResult } from '@/hooks/types/result';
import { useMedia } from '@/hooks/useMedia/useMedia';
import { StudentProfile } from '@/types/student';
import { formatName } from '@/utils/name';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicator from '@mui/icons-material/DragIndicator';
import LanIcon from '@mui/icons-material/Lan';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DialogProps } from '@toolpad/core/useDialogs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ClientLabel } from '../../../types/session';
import { StudentProgress } from './StudentProgress';

interface ClientItemProps {
    item: ClientLabel;
    result?: StudentResult;
    loading?: boolean;
    onDeallocate: (clientId: string) => Promise<void>;
}

const ClientItem: React.FC<ClientItemProps> = ({ item, result, loading, onDeallocate }) => {
    const { isMobile } = useMedia();

    const isAssigned = item.studentProfileId?.length > 0;

    const { setNodeRef, isOver } = useDroppable({
        id: item.socketId,
        disabled: isAssigned,
    });

    const colorArray = ['#FF8282', '#FFB31F', '#FFDE33', '#82FF9D', '#4BF4FF', '#98C1FF', '#F582FF'];
    const textColor = isAssigned ? 'black' : 'white';
    const backgroundColor = isAssigned ? colorArray[item.clientNumber % colorArray.length] : 'black';

    return (
        <Box
            sx={(theme) => ({
                borderRadius: 0.5,
                backgroundColor: backgroundColor,
                border: isOver ? '1px dashed' : '1px solid',
                borderColor: (theme.vars || theme).palette.grey[500],
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                opacity: isOver ? 0.5 : 1,
            })}
            aria-label={item.deviceLabel}
        >
            <Box
                ref={setNodeRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {!isAssigned && (
                    <Typography
                        sx={{
                            color: textColor,
                            textAlign: 'center',
                        }}
                        fontSize={isMobile ? 10 : 12}
                    >
                        {`${item.deviceLabel}`}
                    </Typography>
                )}

                {isAssigned && (
                    <>
                        <>
                            <Typography
                                sx={(theme) => ({
                                    backgroundColor: (theme.vars || theme).palette.grey[900],
                                    color: 'white',
                                    textAlign: 'center',
                                    borderRadius: '4px',
                                    px: 1,
                                    py: 0.25,
                                    my: 0.5,
                                })}
                                fontSize={isMobile ? 10 : 12}
                            >
                                {`${item.deviceLabel}`}
                            </Typography>
                            <Typography
                                sx={{
                                    color: textColor,
                                    width: '100%',
                                    textAlign: 'center',
                                    px: 2,
                                }}
                                fontSize={isMobile ? 10 : 14}
                                gutterBottom
                                noWrap
                            >
                                {`${item.studentName}${item.studentNumber ? ` (${item.studentNumber})` : ''}`}
                            </Typography>
                        </>
                        <Box sx={{ position: 'absolute', width: '100%', bottom: 8, px: 1 }}>
                            <StudentProgress
                                value={
                                    result
                                        ? {
                                              score: result.responseCount,
                                              total: result.questionCount,
                                          }
                                        : undefined
                                }
                            />
                        </Box>
                    </>
                )}
            </Box>
            {isAssigned && item.isGuest && (
                <IconButton
                    onClick={async () => {
                        await onDeallocate?.(item.socketId);
                    }}
                    sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        p: 0.5,
                    }}
                >
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            )}
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'background.default',
                        opacity: 0.5,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

interface StudentItemProps {
    item: StudentProfile;
    result?: StudentResult;
}

const StudentItem: React.FC<StudentItemProps> = ({ item, result }) => {
    const { isMobile } = useMedia();
    return (
        <Box
            sx={(theme) => ({
                display: 'flex',
                cursor: 'move',
                flexDirection: 'row',
                alignItems: 'center',
                borderRadius: 0.5,
                backgroundColor: (theme.vars || theme).palette.background.default,
                position: 'relative',
                mx: 1,
                overflow: 'hidden',
                touchAction: isMobile ? 'inherit' : 'none',
                boxShadow: theme.shadows[1],
                ...theme.applyStyles('dark', {
                    backgroundColor: (theme.vars || theme).palette.grey[800],
                }),
            })}
            aria-label={`student-${item.givenName}-${item.familyName}`}
        >
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    overflow: 'auto',
                    p: 1,
                }}
                onPointerDown={(e) => {
                    if (isMobile) {
                        // stop dragging on mobile so that scroll works correctly on the label part of the view
                        e.stopPropagation();
                    }
                }}
            >
                <Typography
                    noWrap
                    gutterBottom
                    sx={{
                        mx: 1,
                    }}
                >
                    {`${formatName(item.familyName, item.givenName)}${item.studentNumber ? ` (${item.studentNumber})` : ''}`}
                </Typography>
                <StudentProgress
                    value={
                        result
                            ? {
                                  score: result.responseCount,
                                  total: result.questionCount,
                              }
                            : undefined
                    }
                />
            </Box>
            <div style={{ touchAction: 'none', paddingRight: 8, display: 'flex' }}>
                <DragIndicator />
            </div>
        </Box>
    );
};

const StudentDraggableItem: React.FC<StudentItemProps> = ({ item, result }) => {
    const { isDragging, listeners, setNodeRef, attributes } = useDraggable({ id: item.id });
    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={{
                opacity: isDragging ? 0.5 : undefined,
            }}
        >
            <StudentItem item={item} result={result} />
        </div>
    );
};

export function ClassroomAssignStudentDialog({
    payload,
    open,
    onClose,
}: DialogProps<{
    otp?: string;
    clients?: ClientLabel[];
    students?: StudentProfile[];
    onAllocate?: (studentProfile: StudentProfile, clientId: string) => void;
    onDeallocate?: (clientId: string) => void;
    onAutoAllocate?: () => Promise<void>;
    onDeallocateAll?: () => Promise<void>;
    results?: Record<string, StudentResult>;
}>) {
    // hooks
    const { t } = useTranslation();
    const { isMobile } = useMedia();

    // ui state
    const [draggingStudentItem, setDraggingStudentItem] = React.useState<StudentProfile | undefined>();
    const [allocatingClientItems, setAllocatingClientItems] = React.useState<ClientLabel[]>([]);
    const [deallocatingClientItem, setDeallocatingClientItem] = React.useState<ClientLabel[]>([]);
    const [autoAllocating, setAutoAllocating] = React.useState(false);
    const [deallocatingAll, setDeallocatingAll] = React.useState(false);

    const handleDeallocateClient = async (clientId: string) => {
        setDeallocatingClientItem((prev) => {
            const client = payload?.clients?.find((client) => client.socketId === clientId);
            return client ? [...prev, client] : prev;
        });
        await payload.onDeallocate?.(clientId);
        setDeallocatingClientItem((prev) => {
            return prev.filter((client) => client.socketId !== clientId);
        });
    };

    const handleDragStart = (event: DragStartEvent) => {
        const studentItem = payload?.students?.find((student) => {
            return student.id === event.active.id;
        });
        setDraggingStudentItem(studentItem);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { over } = event;

        if (draggingStudentItem && over?.id) {
            setAllocatingClientItems((prev) => {
                const client = payload?.clients?.find((client) => client.socketId === over.id);
                return client ? [...prev, client] : prev;
            });
            await payload.onAllocate?.(draggingStudentItem, String(over.id));
            setAllocatingClientItems((prev) => {
                return prev.filter((client) => client.socketId !== over.id);
            });
        }

        setDraggingStudentItem(undefined);
    };

    const handleDragCancel = () => {
        setDraggingStudentItem(undefined);
    };

    return (
        <Dialog fullWidth maxWidth='xl' disableEscapeKeyDown={true} open={open} fullScreen={isMobile} sx={{ '& .MuiDialog-paper': { borderRadius: isMobile ? 0 : undefined } }}>
            <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragAbort={handleDragCancel} onDragCancel={handleDragCancel}>
                <DialogTitle>{t('classroom.assign.assignStudents')}</DialogTitle>
                <Box sx={{ px: 3 }}>
                    <Stack spacing={2}>
                        <DialogContentText>{t('classroom.assign.assignStudentsDescription')}</DialogContentText>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1,
                                alignItems: 'flex-end',
                            }}
                        >
                            <Typography variant='h5'>
                                {t('classroom.label.otp').toLocaleUpperCase()}: {payload.otp}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button
                                loading={deallocatingAll}
                                onClick={async () => {
                                    setDeallocatingAll(true);
                                    await payload.onDeallocateAll?.();
                                    setDeallocatingAll(false);
                                }}
                                sx={{ minWidth: 'max-content' }}
                            >
                                {t('classroom.assign.assignClear')}
                            </Button>
                            <Button
                                variant='outlined'
                                startIcon={<LanIcon />}
                                loading={autoAllocating}
                                onClick={async () => {
                                    setAutoAllocating(true);
                                    await payload.onAutoAllocate?.();
                                    setAutoAllocating(false);
                                }}
                                sx={{ minWidth: 'max-content' }}
                            >
                                {t('classroom.assign.autoAssign')}
                            </Button>
                        </Box>
                    </Stack>
                </Box>

                <DialogContent sx={{ display: 'flex', px: isMobile ? 1 : undefined }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'row',
                            gap: isMobile ? 1 : 2,
                            minHeight: '50svh',
                            overflowY: 'auto',
                        }}
                    >
                        {/* student list */}
                        <Box
                            sx={(theme) => ({
                                minHeight: '50svh',
                                width: isMobile ? '50%' : 240,
                                overflowY: 'auto',
                                backgroundColor: (theme.vars || theme).palette.background.default,
                                ...theme.applyStyles('light', {
                                    backgroundColor: (theme.vars || theme).palette.grey[100],
                                }),
                            })}
                        >
                            <Stack
                                sx={{
                                    minHeight: '50svh',
                                    overflowY: 'auto',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        m: 2,
                                    }}
                                >
                                    <Typography variant='caption'>{t('classroom.assign.studentList')}</Typography>
                                </Box>
                                <Stack spacing={1} sx={{ mb: 1 }}>
                                    {payload?.students
                                        ?.filter((student) => payload.clients?.findIndex((client) => client.studentProfileId === student.id) === -1)
                                        .map((student) => <StudentDraggableItem key={student.id} item={student} result={payload.results?.[student.id]} />)}
                                </Stack>
                            </Stack>
                        </Box>
                        {/* device list */}
                        <Box
                            sx={(theme) => ({
                                backgroundColor: (theme.vars || theme).palette.background.default,
                                flex: 1,
                                overflow: 'auto',
                                p: 1,
                                position: 'relative',
                                ...theme.applyStyles('light', {
                                    backgroundColor: (theme.vars || theme).palette.grey[100],
                                }),
                            })}
                        >
                            {payload?.clients?.length === 0 ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography>{t('classroom.assign.noDevices')}</Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={1} columns={{ xs: 2, sm: 4, md: 6, lg: 10, xl: 12 }}>
                                    {payload.clients?.map((client, index) => (
                                        <Grid key={`${client.deviceLabel}-${index}`} size={2} sx={{ aspectRatio: 3 / 2, height: 'auto', minWidth: isMobile ? 140 : 160 }}>
                                            <ClientItem
                                                item={client}
                                                result={payload.results?.[client.studentProfileId]}
                                                onDeallocate={handleDeallocateClient}
                                                loading={[...allocatingClientItems, ...deallocatingClientItem].findIndex((c) => c.socketId === client.socketId) !== -1}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant='contained'
                        disabled={payload?.clients?.every((client) => !client.studentProfileId)}
                        onClick={async () => {
                            await onClose();
                        }}
                    >
                        {t('classroom.assign.startLesson')}
                    </Button>
                </DialogActions>

                <DragOverlay
                    dropAnimation={{
                        sideEffects: ({ active, dragOverlay }) => {
                            active.node.style.opacity = '0.5';
                            dragOverlay.node.style.opacity = '0';
                            return () => {
                                active.node.style.opacity = '1';
                            };
                        },
                    }}
                >
                    {draggingStudentItem ? <StudentItem item={draggingStudentItem} result={payload.results?.[draggingStudentItem.id]} /> : null}
                </DragOverlay>
            </DndContext>
        </Dialog>
    );
}
